import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import {
  contrastForeground,
  darkenHexColor,
  lightenHexColor,
  normalizeHexColor,
  rgbTupleFromHex,
} from '@/lib/utils/color';

export type BaseTheme = 'light' | 'dark' | 'pastel-light' | 'pastel-dark';
export type PresetAccentColor =
  | 'blue'
  | 'emerald'
  | 'purple'
  | 'orange'
  | 'pink'
  | 'cyan'
  | 'lavender'
  | 'mint'
  | 'peach'
  | 'rose'
  | 'sky';
export type AccentColor = PresetAccentColor | 'custom';

interface AccentConfig {
  id: AccentColor;
  name: string;
  colors: {
    primary: string;
    accent: string;
    secondary: string;
  };
}

export const accentConfigs: Record<PresetAccentColor, AccentConfig> = {
  blue: {
    id: 'blue',
    name: 'Blue',
    colors: {
      primary: '#3b82f6',
      accent: '#2563eb', // a11y: was #60a5fa — white text on it was 2.54:1 (tabs/buttons); blue-600 passes 5.17:1
      secondary: '#1d4ed8',
    },
  },
  emerald: {
    id: 'emerald',
    name: 'Emerald',
    colors: {
      primary: '#10b981',
      accent: '#34d399',
      secondary: '#047857',
    },
  },
  purple: {
    id: 'purple',
    name: 'Purple',
    colors: {
      primary: '#8b5cf6',
      accent: '#a78bfa',
      secondary: '#6d28d9',
    },
  },
  orange: {
    id: 'orange',
    name: 'Orange',
    colors: {
      primary: '#f97316',
      accent: '#fb923c',
      secondary: '#c2410c',
    },
  },
  pink: {
    id: 'pink',
    name: 'Pink',
    colors: {
      primary: '#ec4899',
      accent: '#f472b6',
      secondary: '#be185d',
    },
  },
  cyan: {
    id: 'cyan',
    name: 'Cyan',
    colors: {
      primary: '#06b6d4',
      accent: '#22d3ee',
      secondary: '#0891b2',
    },
  },
  lavender: {
    id: 'lavender',
    name: 'Lavender',
    colors: {
      primary: '#c4b5fd',
      accent: '#ddd6fe',
      secondary: '#a78bfa',
    },
  },
  mint: {
    id: 'mint',
    name: 'Mint',
    colors: {
      primary: '#86efac',
      accent: '#bbf7d0',
      secondary: '#4ade80',
    },
  },
  peach: {
    id: 'peach',
    name: 'Peach',
    colors: {
      primary: '#fdba74',
      accent: '#fed7aa',
      secondary: '#fb923c',
    },
  },
  rose: {
    id: 'rose',
    name: 'Rose',
    colors: {
      primary: '#fda4af',
      accent: '#fecdd3',
      secondary: '#fb7185',
    },
  },
  sky: {
    id: 'sky',
    name: 'Sky',
    colors: {
      primary: '#7dd3fc',
      accent: '#bae6fd',
      secondary: '#38bdf8',
    },
  },
};

interface ThemeConfig {
  baseTheme: BaseTheme;
  accentColor: AccentColor;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
  };
}

interface ThemeContextType {
  baseTheme: BaseTheme;
  accentColor: AccentColor;
  customAccentColor: string | null;
  themeConfig: ThemeConfig;
  availableAccents: AccentConfig[];
  toggleBaseTheme: () => void;
  setBaseTheme: (theme: BaseTheme) => void;
  setAccentColor: (color: AccentColor) => void;
  setCustomAccentColor: (color: string | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const DEFAULT_CUSTOM_ACCENT = '#a855f7';

function createCustomAccentConfig(hexColor: string | null): AccentConfig | null {
  const normalized = normalizeHexColor(hexColor);
  if (!normalized) {
    return null;
  }

  return {
    id: 'custom',
    name: 'Custom',
    colors: {
      primary: normalized,
      accent: lightenHexColor(normalized, 0.25),
      secondary: darkenHexColor(normalized, 0.2),
    },
  };
}

// Storage keys for theme preferences
const STORAGE_KEYS = {
  baseTheme: 'til-base-theme',
  accentColor: 'til-accent-color',
  customAccent: 'til-custom-accent-color',
} as const;

// Default values for SSR
const DEFAULT_BASE_THEME: BaseTheme = 'light';
const DEFAULT_ACCENT_COLOR: AccentColor = 'blue';
const DEFAULT_CUSTOM_ACCENT_VALUE: string | null = null;

// In-memory cache for client-side values (prevents flash on navigation)
let clientThemeCache: {
  baseTheme: BaseTheme;
  accentColor: AccentColor;
  customAccent: string | null;
} | null = null;

// Listeners for storage changes (for cross-tab sync)
const themeListeners = new Set<() => void>();

function getStorageValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') {
    return defaultValue;
  }
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) {
      return defaultValue;
    }
    return JSON.parse(stored) as T;
  } catch {
    return defaultValue;
  }
}

function setStorageValue<T>(key: string, value: T): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(value));
    // Clear cache so next read gets fresh value
    clientThemeCache = null;
    // Notify listeners for cross-component sync
    themeListeners.forEach((listener) => listener());
  } catch {
    // Storage might be full or disabled
  }
}

// useSyncExternalStore helpers
function subscribeToThemeChanges(callback: () => void): () => void {
  // Subscribe to storage events (cross-tab sync)
  const handleStorageChange = (e: globalThis.StorageEvent): void => {
    if (
      e.key &&
      Object.values(STORAGE_KEYS).includes(
        e.key as (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS]
      )
    ) {
      // Clear cache when storage changes from another tab
      clientThemeCache = null;
      callback();
    }
  };

  // Subscribe to in-memory listeners (same-tab sync)
  themeListeners.add(callback);

  window.addEventListener('storage', handleStorageChange);

  return () => {
    themeListeners.delete(callback);
    window.removeEventListener('storage', handleStorageChange);
  };
}

// Server snapshot returns defaults (no flash because we don't need to wait for mount)
// Cache server snapshot to avoid infinite loop with useSyncExternalStore
const SERVER_THEME_SNAPSHOT = {
  baseTheme: DEFAULT_BASE_THEME,
  accentColor: DEFAULT_ACCENT_COLOR,
  customAccent: DEFAULT_CUSTOM_ACCENT_VALUE,
};

function getServerThemeSnapshot() {
  return SERVER_THEME_SNAPSHOT;
}

// Client snapshot returns localStorage values (immediately available, no useEffect delay)
function getClientThemeSnapshot() {
  // Use cache if available (prevents redundant localStorage reads)
  if (clientThemeCache) {
    return clientThemeCache;
  }

  const snapshot = {
    baseTheme: getStorageValue<BaseTheme>(STORAGE_KEYS.baseTheme, DEFAULT_BASE_THEME),
    accentColor: getStorageValue<AccentColor>(STORAGE_KEYS.accentColor, DEFAULT_ACCENT_COLOR),
    customAccent: getStorageValue<string | null>(
      STORAGE_KEYS.customAccent,
      DEFAULT_CUSTOM_ACCENT_VALUE
    ),
  };

  clientThemeCache = snapshot;
  return snapshot;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // useSyncExternalStore provides SSR-safe, flash-free theme values
  // On server: returns defaults
  // On client: returns localStorage values immediately (no useEffect delay)
  const themeSnapshot = useSyncExternalStore(
    subscribeToThemeChanges,
    getClientThemeSnapshot,
    getServerThemeSnapshot
  );

  const { baseTheme, accentColor, customAccent: customAccentColorRaw } = themeSnapshot;

  const customAccentColor = customAccentColorRaw ?? DEFAULT_CUSTOM_ACCENT;

  const customAccentConfig = useMemo(
    () => createCustomAccentConfig(customAccentColorRaw),
    [customAccentColorRaw]
  );

  // Generate theme configuration based on base theme and accent color
  const themeConfig = useMemo((): ThemeConfig => {
    // Handle hex values by mapping them to preset color names
    let effectiveAccentColor: PresetAccentColor | 'custom' = accentColor;

    // If accentColor is a hex value (starts with #), map it to the appropriate preset name
    if (typeof accentColor === 'string' && accentColor.startsWith('#')) {
      const hexColor = accentColor as string;
      if (hexColor === '#3b82f6') {
        effectiveAccentColor = 'blue';
      } else if (hexColor === '#8b5cf6') {
        effectiveAccentColor = 'purple';
      } else if (hexColor === '#10b981') {
        effectiveAccentColor = 'emerald';
      } else if (hexColor === '#f97316') {
        effectiveAccentColor = 'orange';
      } else {
        effectiveAccentColor = 'custom';
      }
    }

    const accent =
      effectiveAccentColor === 'custom'
        ? (customAccentConfig ?? createCustomAccentConfig(customAccentColor) ?? accentConfigs.blue)
        : (accentConfigs[effectiveAccentColor as PresetAccentColor] ?? accentConfigs.blue);

    let baseColors;

    switch (baseTheme) {
      case 'light':
        baseColors = {
          background: '#ffffff',
          surface: '#f8fafc',
          text: '#0f172a',
          textSecondary: '#64748b',
        };
        break;
      case 'dark':
        baseColors = {
          background: '#0f172a',
          surface: '#1e293b',
          text: '#f8fafc',
          textSecondary: '#94a3b8',
        };
        break;
      case 'pastel-light':
        baseColors = {
          background: '#fdfcfb',
          surface: '#f9f7f4',
          text: '#3a3530',
          textSecondary: '#8b7d6b',
        };
        break;
      case 'pastel-dark':
        baseColors = {
          background: '#1a1a1a',
          surface: '#2a2a2a',
          text: '#e8e8e8',
          textSecondary: '#b8b8b8',
        };
        break;
      default:
        baseColors = {
          background: '#ffffff',
          surface: '#f8fafc',
          text: '#0f172a',
          textSecondary: '#64748b',
        };
    }

    return {
      baseTheme,
      accentColor,
      colors: {
        ...baseColors,
        primary: accent.colors.primary,
        accent: accent.colors.accent,
        secondary: accent.colors.secondary,
      },
    };
  }, [baseTheme, accentColor, customAccentConfig, customAccentColor]);

  const availableAccents = useMemo(() => {
    const presets = Object.values(accentConfigs);
    if (customAccentConfig) {
      return [...presets, customAccentConfig];
    }
    return presets;
  }, [customAccentConfig]);

  // Setter functions that update localStorage and trigger re-render via useSyncExternalStore
  const handleSetBaseTheme = useCallback(
    (newTheme: BaseTheme | ((prev: BaseTheme) => BaseTheme)) => {
      const resolvedTheme = typeof newTheme === 'function' ? newTheme(baseTheme) : newTheme;
      setStorageValue(STORAGE_KEYS.baseTheme, resolvedTheme);
    },
    [baseTheme]
  );

  const handleSetAccentColor = useCallback(
    (newColor: AccentColor | ((prev: AccentColor) => AccentColor)) => {
      const resolvedColor = typeof newColor === 'function' ? newColor(accentColor) : newColor;
      setStorageValue(STORAGE_KEYS.accentColor, resolvedColor);
    },
    [accentColor]
  );

  const handleSetCustomAccentColorRaw = useCallback((newColor: string | null) => {
    setStorageValue(STORAGE_KEYS.customAccent, newColor);
  }, []);

  const toggleBaseTheme = useCallback(() => {
    const nextTheme: BaseTheme =
      baseTheme === 'light'
        ? 'dark'
        : baseTheme === 'dark'
          ? 'pastel-light'
          : baseTheme === 'pastel-light'
            ? 'pastel-dark'
            : 'light';
    handleSetBaseTheme(nextTheme);
  }, [baseTheme, handleSetBaseTheme]);

  const setCustomAccentColor = useCallback(
    (color: string | null) => {
      const normalized = normalizeHexColor(color);
      handleSetCustomAccentColorRaw(normalized);
      if (!normalized && accentColor === 'custom') {
        handleSetAccentColor('blue');
      }
    },
    [accentColor, handleSetAccentColor, handleSetCustomAccentColorRaw]
  );

  const contextValue = useMemo(
    () => ({
      baseTheme,
      accentColor,
      customAccentColor: customAccentConfig?.colors.primary ?? null,
      themeConfig,
      availableAccents,
      toggleBaseTheme,
      setBaseTheme: handleSetBaseTheme,
      setAccentColor: handleSetAccentColor,
      setCustomAccentColor,
    }),
    [
      baseTheme,
      accentColor,
      customAccentConfig,
      themeConfig,
      availableAccents,
      toggleBaseTheme,
      handleSetBaseTheme,
      handleSetAccentColor,
      setCustomAccentColor,
    ]
  );

  // Update CSS variables when theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Diff-then-write: the accent picker re-runs this effect per pointer
    // sample, and an unconditional setProperty + className reset invalidated
    // document style (~20 unchanged properties → full recalc + re-raster of
    // the blurred wash layers) on every sample. Only changed values are
    // written, so a drag costs the variables that actually moved.
    const root = document.documentElement;
    const dark = baseTheme.includes('dark');
    const inputColor = (() => {
      switch (baseTheme) {
        case 'light':
          return '#f8fafc'; // Light slate gray - subtle background
        case 'dark':
          return '#1e293b'; // Dark slate - subtle contrast
        case 'pastel-light':
          return '#f5f3f0'; // Warm cream - matches pastel theme
        case 'pastel-dark':
          return '#252525'; // Soft dark - less harsh than pure black
        default:
          return '#f8fafc';
      }
    })();

    const nextVars: Record<string, string> = {
      '--color-primary': themeConfig.colors.primary,
      '--color-secondary': themeConfig.colors.secondary,
      '--color-accent': themeConfig.colors.accent,
      '--color-on-primary': contrastForeground(themeConfig.colors.primary),
      // on-accent must pair with the tint --color-accent actually renders
      // (colors.accent), so the threshold pick is made against the real bg (#902).
      '--color-on-accent': contrastForeground(themeConfig.colors.accent),
      '--color-accent-rgb': rgbTupleFromHex(themeConfig.colors.primary),
      '--accent-rgb': rgbTupleFromHex(themeConfig.colors.primary),
      '--color-background': themeConfig.colors.background,
      '--color-surface': themeConfig.colors.surface,
      '--color-text-primary': themeConfig.colors.text,
      '--color-text-secondary': themeConfig.colors.textSecondary,
      // Surface variations for proper layering
      '--color-surface-secondary': dark ? '#1f2937' : '#f9fafb',
      '--color-surface-tertiary': dark ? '#374151' : '#f3f4f6',
      // Card background - lighter in dark mode for better visibility
      '--color-card': dark ? '#293548' : '#f9fafb',
      '--color-border': dark ? '#374151' : '#e5e7eb',
      '--color-input': inputColor,
    };
    for (const [property, value] of Object.entries(nextVars)) {
      if (root.style.getPropertyValue(property) !== value) {
        root.style.setProperty(property, value);
      }
    }

    // Resetting the whole className re-ran selector matching for the entire
    // document even when the theme was unchanged — the expensive half of
    // every picker-drag sample. Touch the DOM only on a real change.
    const nextClassName = `theme-${baseTheme}`;
    if (root.className !== nextClassName) {
      root.className = nextClassName;
    }
    const nextDataTheme = dark ? 'dark' : 'light';
    if (root.getAttribute('data-theme') !== nextDataTheme) {
      root.setAttribute('data-theme', nextDataTheme);
    }
    // boneyard reads `.dark` on <html> for dark-mode bones (it does not use
    // data-theme/prefers-color-scheme). Map `dark` + `pastel-dark` → `.dark`.
    // Runs after the className write above so the class survives it.
    root.classList.toggle('dark', dark);
  }, [baseTheme, accentColor, themeConfig]);

  // Always render children - useSyncExternalStore handles SSR/client hydration automatically
  // without the flash that useEffect + useState causes
  // suppressHydrationWarning prevents React hydration warnings for theme-dependent rendering
  return (
    <ThemeContext.Provider value={contextValue}>
      <span suppressHydrationWarning>{children}</span>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
